import React, { useState } from 'react';
import Breadcrumb from './Breadcrumb';
import EmersonHeader from '../components/header/EmersonHeader';
import FooterTwo from '../components/footer/FooterTwo';
import { useProductDocuments } from '../services/productDocumentService';

function ProductDocumentation() {
    const { data: productDocsResponse, isLoading } = useProductDocuments();
    const productDocs = productDocsResponse?.data || [];
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 9; // 3 columns x 3 rows

    const breadcrumbs = [
        { label: 'Home', link: '/' },
        { label: 'Resources', link: '#' },
        { label: 'Product Documentation' }
    ];

    // Calculate pagination
    const totalPages = Math.ceil(productDocs.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentDocs = productDocs.slice(startIndex, endIndex);

    // Handle page change
    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            // Scroll to top of section
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // Generate page numbers to display
    const getPageNumbers = () => {
        const pages = [];
        const maxVisiblePages = 4;
        
        if (totalPages <= maxVisiblePages) {
            // Show all pages if total is less than max
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            // Show first page
            pages.push(1);
            
            // Show pages around current page
            const start = Math.max(2, currentPage - 1);
            const end = Math.min(totalPages - 1, currentPage + 1);
            
            if (start > 2) {
                pages.push('...');
            }
            
            for (let i = start; i <= end; i++) {
                if (i !== 1 && i !== totalPages) {
                    pages.push(i);
                }
            }
            
            if (end < totalPages - 1) {
                pages.push('...');
            }
            
            // Show last page
            if (totalPages > 1) {
                pages.push(totalPages);
            }
        }
        
        return pages;
    };

    return (
        <div className="product-doc-page">
            <EmersonHeader />
            <Breadcrumb title="Product Documentation" breadcrumbs={breadcrumbs} />

            <section className="product-doc-hero rts-section-gap">
                <div className="container">
                    <div className="product-doc-hero-card">
                        <span className="tagline">Technical Resources</span>
                        <h1>Product Documentation & Manuals</h1>
                        <p>
                            Access comprehensive technical documentation, installation guides, operation manuals,
                            and maintenance procedures for all Sembian products. Download PDFs for offline reference
                            and detailed specifications.
                        </p>
                    </div>
                </div>
            </section>

            <section className="product-doc-grid rts-section-gapTop">
                <div className="container">
                    {isLoading ? (
                        <div className="text-center py-5">
                            <p>Loading product documentation...</p>
                        </div>
                    ) : currentDocs.length > 0 ? (
                        <>
                            <div className="row g-4">
                                {currentDocs.map((doc, index) => (
                                    <div key={doc.id || index} className="col-lg-4 col-md-6">
                                        <div className="product-doc-card">
                                            <div className="product-doc-meta">
                                                <span className="category"></span>
                                                <span className="version">Updated: {new Date(doc.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                                            </div>
                                            <h3>{doc.name}</h3>
                                            <p style={{textAlign: 'justify'}}>{doc.description}</p>
                                          
                                            <div className="product-doc-actions">
                                                <a
                                                    href={`${process.env.REACT_APP_API_URL}/assets/images/productdoc/${doc.pdf}`}
                                                    className="rts-btn btn-primary-2"
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    <i className="fas fa-eye"></i> View PDF
                                                </a>
                                                
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* pagination area */}
                            {totalPages > 1 && (
                                <div className="row mt--30">
                                    <div className="col-12">
                                        <div className="text-center">
                                            <div className="pagination">
                                                {/* Previous button */}
                                                <button 
                                                    onClick={() => handlePageChange(currentPage - 1)}
                                                    disabled={currentPage === 1}
                                                    className={currentPage === 1 ? 'disabled' : ''}
                                                    style={{ opacity: currentPage === 1 ? 0.5 : 1, cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
                                                >
                                                    <i className="fal fa-angle-double-left" />
                                                </button>

                                                {/* Page numbers */}
                                                {getPageNumbers().map((page, index) => (
                                                    page === '...' ? (
                                                        <span key={`ellipsis-${index}`} style={{ padding: '15px', marginRight: '10px' }}>...</span>
                                                    ) : (
                                                        <button
                                                            key={page}
                                                            onClick={() => handlePageChange(page)}
                                                            className={currentPage === page ? 'active' : ''}
                                                        >
                                                            {String(page).padStart(2, '0')}
                                                        </button>
                                                    )
                                                ))}

                                                {/* Next button */}
                                                <button 
                                                    onClick={() => handlePageChange(currentPage + 1)}
                                                    disabled={currentPage === totalPages}
                                                    className={currentPage === totalPages ? 'disabled' : ''}
                                                    style={{ opacity: currentPage === totalPages ? 0.5 : 1, cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
                                                >
                                                    <i className="fal fa-angle-double-right" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="text-center py-5">
                            <p>No product documentation available at the moment.</p>
                        </div>
                    )}
                </div>
            </section>

            <FooterTwo />
        </div>
    );
}

export default ProductDocumentation;

